import {
  InstrumentoArquivoAnexo,
  InstrumentoAvaliacaoRegistro,
  InstrumentoPeriodoOption
} from '../services/instrumento-avaliacao.service';
import {
  InstrumentoAlteracaoDetalhada,
  InstrumentoArquivoState,
  InstrumentoCampoAdicionalConfig,
  InstrumentoFormularioState,
  InstrumentoResumoView,
  InstrumentoTurmaOption,
  TipoInstrumentoAvaliacaoId
} from '../models/instrumento-avaliacao.types';
import { ItemEducacional, Periodo } from '../models/nota-types';
import { FormulaCalculo } from '../../../core/config/services/avaliacao.service';
import { normalizarCampoAdicionalParaPayload } from '@features/instrumentos-avaliacao/utils/instrumento-avaliacao.utils';

interface InstrumentoFormularioContexto {
  turmaId?: number | null;
  itemEducacionalId?: number | null;
  periodoAvaliativoId?: number | null;
  hojeIso?: string;
}

export function createInstrumentoFormulario(contexto: InstrumentoFormularioContexto = {}): InstrumentoFormularioState {
  return {
    version: null,
    versao: null,
    turmaId: contexto.turmaId || null,
    itemEducacionalId: contexto.itemEducacionalId || null,
    periodoAvaliativoId: contexto.periodoAvaliativoId || null,
    tipoInstrumento: 'INSTRUMENTO_AVALIACAO',
    data: contexto.hojeIso || getHojeISO(),
    descricao: '',
    sigla: '',
    conteudo: '',
    criteriosAvaliativos: '',
    instrumentoRecuperacaoId: null,
    notaMinima: '',
    notaMaxima: '',
    peso: ''
  };
}

export function mapInstrumentoToForm(
  registro: InstrumentoAvaliacaoRegistro,
  contexto: InstrumentoFormularioContexto = {}
): InstrumentoFormularioState {
  const registroVersionRaw = (registro as { version?: number | string | null; versao?: number | string | null }).version
    ?? (registro as { versao?: number | string | null }).versao;
  const registroVersion = registroVersionRaw === undefined || registroVersionRaw === null || registroVersionRaw === ''
    ? null
    : Number(registroVersionRaw);

  return {
    id: registro.id,
    version: Number.isNaN(registroVersion) ? null : registroVersion,
    versao: Number.isNaN(registroVersion) ? null : registroVersion,
    turmaId: registro.turma?.id || contexto.turmaId || null,
    itemEducacionalId: registro.itemEducacional?.id || contexto.itemEducacionalId || null,
    periodoAvaliativoId: registro.periodoAvaliativo?.id || contexto.periodoAvaliativoId || null,
    tipoInstrumento: (registro.tipoInstrumento as TipoInstrumentoAvaliacaoId) || 'INSTRUMENTO_AVALIACAO',
    data: normalizeDateToInput(registro.data),
    descricao: registro.descricao || '',
    sigla: registro.sigla || '',
    conteudo: String(registro.conteudo || ''),
    criteriosAvaliativos: String(registro.criteriosAvaliativos || ''),
    instrumentoRecuperacaoId: (registro.instrumentoRecuperacao?.id as number) || null,
    notaMinima: registro.notaMinima === null || registro.notaMinima === undefined ? '' : String(registro.notaMinima),
    notaMaxima: registro.notaMaxima === null || registro.notaMaxima === undefined ? '' : String(registro.notaMaxima),
    peso: registro.peso === null || registro.peso === undefined ? '' : String(registro.peso),
    modoAvaliacao: registro.modoAvaliacao || undefined
  };
}

export function mapInstrumentoArquivos(arquivos: InstrumentoArquivoAnexo[] | null | undefined): InstrumentoArquivoState[] {
  return (arquivos || []).map((arquivo) => mapInstrumentoArquivo(arquivo));
}

export function mapInstrumentoArquivo(
  arquivo: InstrumentoArquivoAnexo,
  fallbackNome?: string,
  fallbackTamanho?: number,
  fallbackTipo?: string
): InstrumentoArquivoState {
  return {
    id: arquivo.id,
    nome: String(arquivo.nome || fallbackNome || 'Arquivo sem nome'),
    tipo: String(arquivo.tipo || fallbackTipo || ''),
    tamanho: Number(arquivo.tamanho ?? fallbackTamanho ?? 0) || 0,
    raw: { ...arquivo }
  };
}

export function resolveInstrumentoArquivoIdentificador(anexo: InstrumentoArquivoState, index: number): number | string {
  return anexo.id ?? `instrumento-anexo-${index}`;
}

export function toNullableNumber(value: number | string | null): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsedValue = Number(value);
    return Number.isNaN(parsedValue) ? null : parsedValue;
  }

  return null;
}

export function formatarInstrumentoTamanhoArquivo(size?: number): string {
  if (!size || size <= 0) {
    return '0 KB';
  }

  const units = ['B', 'KB', 'MB', 'GB'];
  let sizeValue = size;
  let unitIndex = 0;

  while (sizeValue >= 1024 && unitIndex < units.length - 1) {
    sizeValue /= 1024;
    unitIndex += 1;
  }

  return `${sizeValue.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

export function buildInstrumentoAnexosAccept(extensoes: readonly string[]): string {
  return extensoes.map((extensao) => `.${extensao}`).join(',');
}

export function buildInstrumentoAnexosLabel(extensoes: readonly string[]): string {
  return `${extensoes.map((extensao) => extensao.toUpperCase()).join(', ')}.`;
}

export function serializeInstrumentoForm(formulario: InstrumentoFormularioState): string {
  return JSON.stringify(formulario);
}

export function serializeInstrumentoArquivos(arquivos: InstrumentoArquivoState[]): string {
  return JSON.stringify(
    arquivos.map((arquivo) => ({
      id: arquivo.id ?? null,
      nome: arquivo.nome,
      tamanho: arquivo.tamanho ?? null,
      tipo: arquivo.tipo ?? ''
    }))
  );
}

export function serializeInstrumentoCampoAdicional(campoAdicional: InstrumentoCampoAdicionalConfig): string {
  return JSON.stringify(campoAdicional ?? null);
}

export function cloneInstrumentoCampoAdicional<T>(value: T): T {
  return value === null || value === undefined
    ? value
    : JSON.parse(JSON.stringify(value)) as T;
}

export function parseOptionalNumber(value: string): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const normalized = Number(String(value).replace(',', '.'));
  return Number.isNaN(normalized) ? null : normalized;
}

export function parseUnknownNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : value;
  }

  if (typeof value === 'string') {
    return parseOptionalNumber(value);
  }

  return null;
}

export function normalizeDateToInput(value: unknown): string {
  if (!value || typeof value !== 'string') {
    return '';
  }

  return value.includes('T') ? value.split('T')[0] : value;
}

export function parseDate(value: unknown): Date | null {
  if (!value || typeof value !== 'string') {
    return null;
  }

  const normalized = value.includes('T') ? value : `${value}T00:00:00`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function getHojeISO(): string {
  const hoje = new Date();
  const offset = hoje.getTimezoneOffset();
  const local = new Date(hoje.getTime() - offset * 60000);
  return local.toISOString().split('T')[0];
}

export function getInstrumentoAnexoExtensao(fileName: string): string {
  const partes = String(fileName || '').toLowerCase().split('.');
  return partes.length > 1 ? partes.pop() || '' : '';
}

/**
 * Rótulo de exibição de um tipo de instrumento de avaliação. Override do
 * abstract method da `InstrumentoAvaliacaoHostBase` delega pra esta função.
 */
export function getTipoInstrumentoLabel(tipo: TipoInstrumentoAvaliacaoId | string | null | undefined): string {
  switch (tipo) {
    case 'RECUPERACAO_PARALELA':
      return 'Recuperação paralela';
    case 'RECUPERACAO_PERIODO_AVALIATIVO':
      return 'Recuperação do período';
    case 'PONTO_EXTRA':
      return 'Nota Complementar/Ponto Extra';
    default:
      return 'Instrumento de avaliação';
  }
}

/**
 * Extrai o id da turma de um objeto "raw" (payload cru da API). Override do
 * abstract method da `InstrumentoAvaliacaoHostBase` delega pra esta função.
 */
export function extractTurmaIdFromRaw(raw: Record<string, unknown>): number | null {
  const turma = raw['turma'] as { id?: number | string } | undefined;
  const turmaId = Number(turma?.id || 0);
  return turmaId > 0 ? turmaId : null;
}

/**
 * Converte um `InstrumentoResumoView` (view model já resolvido) de volta pro
 * shape de `InstrumentoAvaliacaoRegistro` (formato da API), reidratando o
 * `raw` original com os campos que podem ter sido atualizados na tela
 * (ex.: estado de publicação).
 */
export function toInstrumentoRegistro(item: InstrumentoResumoView): InstrumentoAvaliacaoRegistro {
  const raw = (item.raw || {}) as InstrumentoAvaliacaoRegistro & {
    publicavel?: { publicado?: boolean; dataPublicacao?: string | null };
    publicar?: boolean;
  };

  return {
    ...raw,
    id: item.id,
    descricao: item.descricao,
    sigla: item.sigla,
    data: item.data,
    tipoInstrumento: item.tipoInstrumento,
    modoAvaliacao: item.modoAvaliacao,
    periodoAvaliativo: item.periodoAvaliativoId
      ? {
          ...(raw.periodoAvaliativo || {}),
          id: item.periodoAvaliativoId
        }
      : raw.periodoAvaliativo || null,
    publicado: item.publico,
    publicar: item.publico,
    dataPublicacao: item.dataPublicacao || raw.dataPublicacao || raw.publicavel?.dataPublicacao || null,
    publicavel: {
      ...(raw.publicavel || {}),
      publicado: item.publico,
      dataPublicacao: item.dataPublicacao || raw.dataPublicacao || raw.publicavel?.dataPublicacao || null
    }
  };
}

/**
 * Rótulo de uma fórmula de cálculo, indicando se é da Secretaria (sem
 * estabelecimento vinculado) ou de um estabelecimento de ensino específico.
 */
export function formatResultFormulaCalculo(formulaCalculo: FormulaCalculo | null): string {
  if (!formulaCalculo) {
    return '';
  }

  return `${formulaCalculo.descricao} - ${!formulaCalculo.estabelecimento ? 'Secretaria' : 'Estabelecimento de ensino'}`;
}

/**
 * Formata uma data/hora ISO de publicação como `dd/mm/aaaa às hh:mmh`. Datas
 * inválidas retornam a string original sem formatar (fallback defensivo).
 */
export function formatarDataHoraPublicacao(dataPublicacao: string): string {
  const data = new Date(dataPublicacao);

  if (Number.isNaN(data.getTime())) {
    return dataPublicacao;
  }

  const dia = String(data.getDate()).padStart(2, '0');
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const ano = data.getFullYear();
  const hora = String(data.getHours()).padStart(2, '0');
  const minuto = String(data.getMinutes()).padStart(2, '0');

  return `${dia}/${mes}/${ano} às ${hora}:${minuto}h`;
}

/**
 * Mescla um registro de instrumento com o resumo correspondente (quando
 * encontrado), priorizando os dados do `raw` do resumo — que costuma vir
 * mais atualizado (ex.: estado de publicação recém-alterado na tela) — sobre
 * o registro original. Sem resumo correspondente, retorna o registro como
 * veio (sem merge).
 */
export function mergeInstrumentoComResumo(
  registro: InstrumentoAvaliacaoRegistro,
  resumo: InstrumentoResumoView | undefined
): InstrumentoAvaliacaoRegistro {
  if (!resumo) {
    return registro;
  }

  const instrumentoId = Number(registro.id || 0);
  const rawResumo = (resumo.raw || {}) as InstrumentoAvaliacaoRegistro & {
    publicavel?: { publicado?: boolean; dataPublicacao?: string | null };
    publicar?: boolean;
  };

  return {
    ...registro,
    ...rawResumo,
    id: instrumentoId,
    descricao: registro.descricao || rawResumo.descricao || resumo.descricao,
    sigla: registro.sigla || rawResumo.sigla || resumo.sigla,
    data: registro.data || rawResumo.data || resumo.data,
    turma: registro.turma || rawResumo.turma,
    itemEducacional: registro.itemEducacional || rawResumo.itemEducacional,
    periodoAvaliativo: registro.periodoAvaliativo || rawResumo.periodoAvaliativo,
    publicavel: rawResumo.publicavel || registro.publicavel,
    publicado: typeof rawResumo.publicado === 'boolean' ? rawResumo.publicado : registro.publicado,
    publicar: typeof rawResumo.publicar === 'boolean' ? rawResumo.publicar : (registro as { publicar?: boolean }).publicar,
    dataPublicacao: rawResumo.dataPublicacao || rawResumo.publicavel?.dataPublicacao || registro.dataPublicacao || null
  };
}

/**
 * Sanitiza um campo numérico (notaMinima/notaMaxima/peso) digitado em texto:
 * troca vírgula por ponto, remove caracteres inválidos, e aplica os limites
 * de configuração (peso máximo fixo, nota máxima da configuração da turma).
 * Nota: o sinal de `-` é removido pela regex de caracteres inválidos ANTES
 * de qualquer checagem de negativo — nota/peso negativos digitados nunca
 * disparam a regra "negativo vira vazio" (comportamento real, replicado tal
 * como estava antes da extração, não é um bug desta função).
 */
export function sanitizeInstrumentoCampoNumerico(
  value: unknown,
  campo: 'notaMinima' | 'notaMaxima' | 'peso' | undefined,
  instrumentoPesoMaximoLimite: number,
  instrumentoConfiguracaoTurma: unknown
): string {
  const raw = String(value ?? '').replace(',', '.');
  const semCaracteresInvalidos = raw.replace(/[^\d.]/g, '');

  const partes = semCaracteresInvalidos.split('.');
  const parteInteira = partes.shift() || '';
  const parteDecimal = partes.join('');
  const sanitizado = parteDecimal ? `${parteInteira}.${parteDecimal}` : parteInteira;

  if (campo === 'peso') {
    const [inteiroPeso = '', decimalPeso = ''] = sanitizado.split('.');
    const inteiroLimitado = inteiroPeso.slice(0, 3);
    const pesoSanitizado = decimalPeso ? `${inteiroLimitado}.${decimalPeso}` : inteiroLimitado;
    const numero = parseOptionalNumber(pesoSanitizado);

    if (numero !== null && numero <= 0) {
      return '';
    }

    if (numero !== null && numero > instrumentoPesoMaximoLimite) {
      return String(instrumentoPesoMaximoLimite);
    }

    return pesoSanitizado;
  } else if (campo === 'notaMinima' || campo === 'notaMaxima') {
    const numero = parseOptionalNumber(sanitizado);
    if (numero !== null && numero < 0) {
      return '';
    }
    const notaMaximaConfiguracao = Number((instrumentoConfiguracaoTurma as { notaMaxima?: number } | null)?.notaMaxima);
    if (campo === 'notaMaxima' && numero !== null && !Number.isNaN(notaMaximaConfiguracao) && numero > notaMaximaConfiguracao) {
      return String(notaMaximaConfiguracao);
    }
  }

  return sanitizado;
}

export interface InstrumentoPayloadContexto {
  registroOriginal: InstrumentoAvaliacaoRegistro | null;
  periodoDetalhado: InstrumentoPeriodoOption | undefined;
  periodoSelecionadoFallback: Periodo | null;
  shouldShowPeriodo: boolean;
  turmaSelecionada: InstrumentoTurmaOption | null;
  itemSelecionado: ItemEducacional | null;
  turmaEAtividade: boolean;
  primeiroPeriodoTipoPeriodo: string | undefined;
  instrumentoRecuperacao: InstrumentoResumoView | null;
  modoAvaliacaoFallback: string | undefined;
  instrumentoRecuperacaoDetalhado: InstrumentoAvaliacaoRegistro | undefined;
  exibeFaixaNotas: boolean;
  exibePeso: boolean;
  instrumentoArquivos: InstrumentoArquivoState[];
  campoAdicionalOriginal: InstrumentoCampoAdicionalConfig;
}

/**
 * Monta o payload de instrumento de avaliação a partir do formulário e do
 * contexto já resolvido pelo componente (turma/item/período selecionados,
 * instrumento de recuperação vinculado, etc.). Em edição, reaproveita o DTO
 * original (`registroOriginal`) como base, removendo os campos de leitura
 * (`publicavel`/`publicado`/`dataPublicacao`/`versao`) antes de sobrepor os
 * campos editáveis do formulário.
 *
 * Nota: `normalizarPayloadRecuperacaoParalela` (herdado de
 * `InstrumentoAvaliacaoHostBase`) continua sendo aplicado pelo COMPONENTE
 * depois de chamar esta função, não foi movido pra dentro dela.
 */
export function construirInstrumentoPayload(
  form: InstrumentoFormularioState,
  contexto: InstrumentoPayloadContexto
): InstrumentoAvaliacaoRegistro {
  const modoAvaliacao = contexto.instrumentoRecuperacao?.modoAvaliacao || contexto.modoAvaliacaoFallback;
  const periodoSelecionado = contexto.shouldShowPeriodo
    ? ((contexto.periodoDetalhado?.periodoAvaliativo as { id: number; descricao?: string; dataInicial?: string; dataFinal?: string } | undefined) || contexto.periodoSelecionadoFallback || null)
    : null;
  const payloadBase = (form.id && contexto.registroOriginal
    ? JSON.parse(JSON.stringify(contexto.registroOriginal))
    : {}) as InstrumentoAvaliacaoRegistro & Record<string, unknown>;
  const version = form.version ?? form.versao ?? undefined;

  delete payloadBase.publicavel;
  delete payloadBase.publicado;
  delete payloadBase.dataPublicacao;
  delete payloadBase.versao;

  payloadBase.id = form.id;

  if (version === undefined || version === null) {
    delete payloadBase.version;
  } else {
    payloadBase.version = version;
  }

  payloadBase.turma = contexto.turmaSelecionada
    ? { ...(payloadBase.turma || {}), ...contexto.turmaSelecionada }
    : payloadBase.turma;
  payloadBase.itemEducacional = contexto.itemSelecionado
    ? { ...(payloadBase.itemEducacional || {}), ...contexto.itemSelecionado }
    : payloadBase.itemEducacional;
  payloadBase.periodoAvaliativo = periodoSelecionado
    ? { ...((payloadBase.periodoAvaliativo as Record<string, unknown> | null) || {}), ...periodoSelecionado }
    : null;
  payloadBase.tipoInstrumento = form.tipoInstrumento || payloadBase.tipoInstrumento || undefined;
  payloadBase.tipoPeriodo = contexto.turmaEAtividade
    ? String(contexto.primeiroPeriodoTipoPeriodo || payloadBase.tipoPeriodo || 'INSTRUMENTO')
    : String(contexto.periodoDetalhado?.tipoPeriodo || payloadBase.tipoPeriodo || 'INSTRUMENTO');
  payloadBase['tipo'] = String(payloadBase['tipo'] || 'INSTRUMENTO');
  payloadBase.modoAvaliacao = modoAvaliacao || payloadBase.modoAvaliacao || undefined;
  payloadBase.data = form.data;
  payloadBase.descricao = form.descricao.trim();
  payloadBase.sigla = form.sigla.trim();
  payloadBase.conteudo = form.conteudo?.trim() || '';
  payloadBase.criteriosAvaliativos = form.criteriosAvaliativos?.trim() || '';
  payloadBase.instrumentoRecuperacao = contexto.instrumentoRecuperacao
    ? {
        ...(((payloadBase.instrumentoRecuperacao as Record<string, unknown> | null) || {})),
        ...(contexto.instrumentoRecuperacaoDetalhado || contexto.instrumentoRecuperacao.raw)
      } as any
    : null;
  payloadBase.notaMinima = contexto.exibeFaixaNotas ? parseOptionalNumber(form.notaMinima) : undefined;
  payloadBase.notaMaxima = contexto.exibeFaixaNotas ? parseOptionalNumber(form.notaMaxima) : undefined;
  payloadBase.peso = contexto.exibePeso ? parseOptionalNumber(form.peso) : undefined;
  payloadBase['arquivos'] = contexto.instrumentoArquivos.map((arquivo) => ({ ...arquivo.raw }));

  const campoAdicionalBruto = contexto.campoAdicionalOriginal
    ? cloneInstrumentoCampoAdicional(contexto.campoAdicionalOriginal)
    : (Array.isArray(payloadBase['campoAdicional']) ? payloadBase['campoAdicional'] : []);
  payloadBase['campoAdicional'] = normalizarCampoAdicionalParaPayload(campoAdicionalBruto, !payloadBase.id);
  payloadBase['competenciaCHA'] = Array.isArray(payloadBase['competenciaCHA']) ? payloadBase['competenciaCHA'] : [];
  payloadBase['objetivoAprendizagem'] = Array.isArray(payloadBase['objetivoAprendizagem']) ? payloadBase['objetivoAprendizagem'] : [];

  return payloadBase;
}

export interface InstrumentoValidacaoContexto {
  labelItemEducacional: string;
  shouldShowPeriodo: boolean;
  turmaEjaModular: boolean;
  turmaEAtividade: boolean;
  instrumentosContexto: InstrumentoResumoView[];
  instrumentosRecuperacaoOptions: InstrumentoResumoView[];
  exibeFaixaNotas: boolean;
  exibePeso: boolean;
  notaMinimaConfiguracao: number;
  notaMaximaConfiguracao: number;
  pesoMinimoLimite: number;
  pesoMaximoLimite: number;
  rangePeriodoSelecionado: { inicio: Date; fim: Date } | null;
  diaLetivoValido: boolean;
  possuiNotasInformadas: boolean;
  alteracoesCriticas: string[];
  alteracoesFormulario: InstrumentoAlteracaoDetalhada[];
  anoLetivoAtual: number | null;
  recuperacaoParalelaVinculada: InstrumentoResumoView | undefined;
  camposAdicionaisObrigatoriosValidos: boolean;
}

/**
 * Consolida as regras de negócio do formulário de instrumento de avaliação
 * antes de salvar/atualizar. O contexto reúne tudo que depende de
 * estado/getters/serviço do componente (turma/período selecionados, dia
 * letivo, instrumentos já cadastrados no contexto, etc.) — a função só
 * aplica as regras e monta a lista de erros.
 *
 * `diaLetivoValido` já vem resolvido pelo componente (a chamada real ao
 * `calendarioService.verificaDiaLetivo` só acontece quando há uma data
 * válida no formulário — mesma condição do `if (data)` original).
 */
export function validarInstrumentoFormulario(
  form: InstrumentoFormularioState,
  contexto: InstrumentoValidacaoContexto
): string[] {
  const erros: string[] = [];
  const descricao = form.descricao.trim();
  const sigla = form.sigla.trim();
  const tipoInstrumento = form.tipoInstrumento;
  const data = parseDate(form.data);
  const notaMinima = parseOptionalNumber(form.notaMinima);
  const notaMaxima = parseOptionalNumber(form.notaMaxima);
  const peso = parseOptionalNumber(form.peso);
  const hoje = parseDate(getHojeISO());

  if (!form.turmaId) {
    erros.push('Informe a turma.');
  }

  if (!form.itemEducacionalId) {
    erros.push(`Informe o ${contexto.labelItemEducacional}.`);
  }

  if (contexto.shouldShowPeriodo && !form.periodoAvaliativoId) {
    erros.push('Informe o período avaliativo.');
  }

  if (!form.data) {
    erros.push('Informe a data do instrumento.');
  }

  if (!tipoInstrumento) {
    erros.push('Informe o tipo de instrumento de avaliação.');
  }

  if (!descricao) {
    erros.push('Informe a descrição do instrumento.');
  }

  if (!sigla) {
    erros.push('Informe a sigla do instrumento.');
  }

  if (sigla.length > 5) {
    erros.push('A sigla do instrumento deve ter no máximo 5 caracteres.');
  }

  // Validação de duplicidade (descrição + sigla) é feita apenas no back-end.

  if (tipoInstrumento === 'RECUPERACAO_PARALELA' && !form.instrumentoRecuperacaoId) {
    erros.push('Selecione o instrumento de avaliação a ser recuperado.');
  }

  if (contexto.turmaEjaModular) {
    erros.push('Não é permitido cadastrar instrumentos de avaliação para turmas de EJA modular.');
  }

  if (tipoInstrumento === 'RECUPERACAO_PARALELA' && form.instrumentoRecuperacaoId) {
    const recuperacaoExistente = contexto.instrumentosContexto.find((item) =>
      item.id !== form.id &&
      item.tipoInstrumento === 'RECUPERACAO_PARALELA' &&
      item.instrumentoRecuperacaoId === form.instrumentoRecuperacaoId
    );

    if (recuperacaoExistente) {
      erros.push('Cada instrumento de avaliação pode possuir apenas uma recuperação paralela.');
    }
  }

  if (tipoInstrumento === 'RECUPERACAO_PERIODO_AVALIATIVO') {
    if (contexto.turmaEAtividade || contexto.turmaEjaModular) {
      erros.push('Recuperação do período avaliativo não está disponível para este tipo de turma.');
    }

    const recuperacaoPeriodoExistente = contexto.instrumentosContexto.find((item) =>
      item.id !== form.id && item.tipoInstrumento === 'RECUPERACAO_PERIODO_AVALIATIVO'
    );

    if (recuperacaoPeriodoExistente) {
      erros.push('Já existe uma recuperação do período avaliativo cadastrada para este contexto.');
    }
  }

  if (contexto.exibeFaixaNotas && ((notaMinima !== null && notaMaxima === null) || (notaMinima === null && notaMaxima !== null))) {
    erros.push('Informe a menor e a maior nota do instrumento de avaliação.');
  }

  if (contexto.exibeFaixaNotas && notaMinima !== null && !Number.isNaN(contexto.notaMinimaConfiguracao) && notaMinima < contexto.notaMinimaConfiguracao) {
    erros.push(`A menor nota deve ser maior ou igual a ${contexto.notaMinimaConfiguracao}.`);
  }

  if (contexto.exibeFaixaNotas && notaMaxima !== null && !Number.isNaN(contexto.notaMaximaConfiguracao) && notaMaxima > contexto.notaMaximaConfiguracao) {
    erros.push(`A maior nota deve ser menor ou igual a ${contexto.notaMaximaConfiguracao}.`);
  }

  if (contexto.exibePeso && peso !== null && peso < contexto.pesoMinimoLimite) {
    erros.push(`O peso deve ser maior ou igual a ${contexto.pesoMinimoLimite}.`);
  }

  if (contexto.exibePeso && peso !== null && peso > contexto.pesoMaximoLimite) {
    erros.push(`O peso deve ser menor ou igual a ${contexto.pesoMaximoLimite}.`);
  }

  if (data && contexto.shouldShowPeriodo) {
    const range = contexto.rangePeriodoSelecionado;
    if ((tipoInstrumento === 'INSTRUMENTO_AVALIACAO' || tipoInstrumento === 'PONTO_EXTRA') && range && (data < range.inicio || data > range.fim)) {
      erros.push('A data do instrumento deve pertencer ao período avaliativo selecionado.');
    }
  }

  if (data && !contexto.diaLetivoValido) {
    erros.push('A data informada deve ser um dia letivo para a turma selecionada.');
  }

  if (data && tipoInstrumento === 'RECUPERACAO_PARALELA') {
    const instrumentoRecuperacao = contexto.instrumentosRecuperacaoOptions.find((item) => item.id === form.instrumentoRecuperacaoId);
    const dataRecuperacao = parseDate(instrumentoRecuperacao?.data);
    if (dataRecuperacao && data <= dataRecuperacao) {
      erros.push('A data da recuperação paralela deve ser maior que a data do instrumento recuperado.');
    }
  }

  if (data && tipoInstrumento === 'RECUPERACAO_PERIODO_AVALIATIVO') {
    const datasInstrumentos = contexto.instrumentosContexto
      .filter((item) => item.tipoInstrumento === 'INSTRUMENTO_AVALIACAO')
      .map((item) => parseDate(item.data))
      .filter((item): item is Date => !!item)
      .sort((a, b) => b.getTime() - a.getTime());

    if (datasInstrumentos.length && data <= datasInstrumentos[0]) {
      erros.push('A data da recuperação do período avaliativo deve ser maior que a data dos instrumentos do período.');
    }
  }

  if (form.id && contexto.possuiNotasInformadas) {
    const alteracoesComNotas = contexto.alteracoesCriticas.filter((campo) => campo !== 'data');
    if (alteracoesComNotas.length) {
      erros.push(`Já existem notas informadas. Só é permitido alterar descrição, sigla, conteúdo, critério avaliativo e data. Campos alterados: ${alteracoesComNotas.join(', ')}.`);
    }

    if (data && hoje && data > hoje) {
      erros.push('Ao alterar a data de um instrumento que possui registro de desempenho, a nova data não pode ser futura.');
    }
  }

  if (
    form.id &&
    form.tipoInstrumento === 'RECUPERACAO_PARALELA' &&
    contexto.alteracoesFormulario.some(({ campo }) => campo === 'periodoAvaliativoId')
  ) {
    erros.push('O período avaliativo de uma recuperação paralela não pode ser alterado manualmente.');
  }

  if (data && tipoInstrumento === 'RECUPERACAO_PERIODO_AVALIATIVO') {
    if (contexto.anoLetivoAtual !== null && data.getFullYear() !== contexto.anoLetivoAtual) {
      erros.push('A data da recuperação do período avaliativo deve estar dentro do ano letivo selecionado.');
    }
  }

  if (form.id && contexto.recuperacaoParalelaVinculada) {
    const alteracoesBloqueadas = contexto.alteracoesCriticas.filter((campo) => campo !== 'data' && campo !== 'período avaliativo');
    if (alteracoesBloqueadas.length) {
      erros.push(`Existe uma recuperação paralela vinculada. Não é permitido alterar: ${alteracoesBloqueadas.join(', ')}.`);
    }
  }

  if (!contexto.camposAdicionaisObrigatoriosValidos) {
    erros.push('Preencha os campos obrigatórios em dados adicionais.');
  }

  return Array.from(new Set(erros));
}
